import { Link, useOutletContext } from "react-router-dom";
import { ClipboardCheck, CalendarCheck, HandHeart, Phone, Mail, ChevronRight } from "lucide-react";

/**
 * Care Seeker Portal home — three primary action tiles + the assigned
 * Care Navigator contact strip (the ONLY staff info exposed in the portal
 * per product spec).
 */
export default function PortalHome() {
  const { me } = useOutletContext();
  const tiles = [
    {
      to: "/portal/assessment",
      Icon: ClipboardCheck,
      title: "Wellness check-in",
      copy: "Take a few minutes to share how you're doing. Your Care Navigator will see your answers.",
      cta: "Start check-in",
      testId: "portal-home-assessment",
      color: "bg-[#E8ECE7]",
    },
    {
      to: "/portal/appointments",
      Icon: CalendarCheck,
      title: "My upcoming visits",
      copy: "See appointments scheduled with HarborWell.",
      cta: "View visits",
      testId: "portal-home-appointments",
      color: "bg-[#DBEAFE]",
    },
    {
      to: "/portal/request-help",
      Icon: HandHeart,
      title: "Ask for help",
      copy: "Need something — food, housing, a referral? Tell us what's going on and we'll follow up.",
      cta: "Send a request",
      testId: "portal-home-request",
      color: "bg-[#FEF3C7]",
    },
  ];

  return (
    <div className="space-y-6" data-testid="portal-home">
      <div>
        <p className="text-xs uppercase tracking-wider text-[#7E937D] font-medium">Welcome back</p>
        <h1 className="font-display text-3xl font-semibold text-[#0F172A] mt-1">
          What would you like to do today?
        </h1>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {tiles.map(({ to, Icon, title, copy, cta, testId, color }) => (
          <Link
            key={to}
            to={to}
            data-testid={testId}
            className="group bg-white border border-[#E5E5E0] rounded-2xl p-5 hover:border-[#1E3A8A]/30 hover:shadow-sm transition-all flex flex-col"
          >
            <div className={`w-12 h-12 rounded-2xl ${color} text-[#1E3A8A] flex items-center justify-center mb-3`}>
              <Icon className="w-6 h-6" strokeWidth={1.75} />
            </div>
            <div className="font-display text-lg font-semibold text-[#0F172A]">{title}</div>
            <p className="text-sm text-[#334155] mt-1 flex-1">{copy}</p>
            <div className="mt-4 inline-flex items-center text-sm font-medium text-[#1E3A8A] group-hover:translate-x-0.5 transition-transform">
              {cta} <ChevronRight className="w-4 h-4 ml-1" />
            </div>
          </Link>
        ))}
      </div>

      {/* Care Navigator strip (only staff info visible to the Care Seeker) */}
      {me.care_navigator?.name && (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5" data-testid="portal-home-navigator">
          <p className="text-xs uppercase tracking-wider text-[#7E937D] font-medium">Your Care Navigator</p>
          <div className="mt-2 flex items-start gap-4 flex-wrap">
            <div>
              <div className="font-medium text-[#0F172A]">{me.care_navigator.name}</div>
              {me.care_navigator.title && (
                <div className="text-xs text-[#334155]">{me.care_navigator.title}</div>
              )}
            </div>
            <div className="flex gap-2 flex-wrap text-sm">
              {me.care_navigator.email && (
                <a href={`mailto:${me.care_navigator.email}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F9F8F6] text-[#1E3A8A] hover:bg-[#E8ECE7]" data-testid="portal-navigator-email">
                  <Mail className="w-3.5 h-3.5" /> {me.care_navigator.email}
                </a>
              )}
              {me.care_navigator.phone && (
                <a href={`tel:${me.care_navigator.phone}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F9F8F6] text-[#1E3A8A] hover:bg-[#E8ECE7]" data-testid="portal-navigator-phone">
                  <Phone className="w-3.5 h-3.5" /> {me.care_navigator.phone}
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
